/**
 * The dataLayer payload guard, shared by every surface that pushes to GTM.
 *
 * Lifted out of `apps/go/src/lib/analytics.ts` when `apps/partner` needed the
 * same thing. It qualifies on all three of the shared-utilities tests in
 * `docs/rules/shared-utilities.md`: two apps, stateless, one small concern —
 * and a second copy of a PII denylist is how one of them ends up missing a key.
 *
 * WHAT IT IS FOR. Spec §14: no phone numbers and no raw identifiers in
 * analytics. Every surface that pushes events holds identifiers for the
 * duration of a render, and every one of them will eventually be tempted to
 * pass one through "just for attribution". Person-level attribution is the
 * server's job — a `referral.link_opened` row, a visit row — never the
 * browser's, because the browser hands it to a third party.
 *
 * The event NAME vocabulary stays per-app: `apps/go` fires `partner_cta_*` on
 * a campaign page and `apps/partner` fires dashboard events, and merging those
 * two lists would put names in each app's autocomplete that its GTM container
 * has no trigger for.
 */

/**
 * Keys that must never reach dataLayer, GA4 or the pixel.
 *
 * Matched case-insensitively, and in both `snake_case` and squashed form,
 * because a payload is hand-written at the call site and `prospectId` is as
 * likely as `prospect_id`.
 */
export const FORBIDDEN_KEYS = [
	"phone",
	"phone_norm",
	"phonenorm",
	"prospect_id",
	"prospectid",
	"partner_id",
	"partnerid",
	"referral_id",
	"referralid",
	"token",
	"visit_token",
	"visittoken",
	"name",
	"firm_name",
	"firmname",
	"email",
	"ip",
	"upi",
	"upi_id",
	"upiid",
	"pan",
	// A referral code identifies one homeowner's page. It is the thing that
	// makes `/e/<code>` personal, so passing it to GA4 "just for attribution"
	// would put a person-level identifier in a third party's analytics.
	"code",
	"referral_code",
	"referralcode",
] as const;

export type AnalyticsParams = Record<
	string,
	string | number | boolean | undefined
>;

/** The offending keys in a payload, or an empty array. */
export function findForbiddenKeys(params: AnalyticsParams): string[] {
	return Object.keys(params).filter((key) =>
		(FORBIDDEN_KEYS as readonly string[]).includes(key.toLowerCase()),
	);
}

/**
 * Build a dataLayer payload: drop undefined values, refuse anything that looks
 * like personal data.
 *
 * Returns null rather than throwing, so a mistake degrades to "this event did
 * not fire" instead of "the page broke" — but `onViolation` exists so it can be
 * loud in dev, because a silently-dropped analytics event is its own kind of
 * bug.
 */
export function buildEvent(
	event: string,
	params: AnalyticsParams = {},
	onViolation?: (keys: string[]) => void,
): Record<string, string | number | boolean> | null {
	const violations = findForbiddenKeys(params);
	if (violations.length > 0) {
		onViolation?.(violations);
		return null;
	}

	const payload: Record<string, string | number | boolean> = { event };
	for (const [key, value] of Object.entries(params)) {
		if (value !== undefined) payload[key] = value;
	}
	return payload;
}
